import { Currency, CurrencyMinorUnits } from "@redotech/money/currencies";

/**
 * Currency-safe `money` namespace injected into artifacts as an ambient global.
 * `distribute` is opt-in so contracts only advertise what they need.
 */
interface MoneyPreludeOptions {
  includeDistribute?: boolean;
}

const currencyUnion = Object.values(Currency)
  .map((c) => `"${c}"`)
  .join(" | ");

const minorUnitExceptionsJson = JSON.stringify(
  Object.fromEntries(
    Object.entries(CurrencyMinorUnits).filter(([, units]) => units !== 2),
  ),
);

const distributeDts = `
  /**
   * Splits a value across proportions without losing or minting cents.
   * Returns one Money per proportion, in order, summing exactly to
   * valueToDistribute (largest-remainder allocation at the currency's
   * minor units). Proportions must be same-currency, non-negative Money
   * values; if they sum to zero, every share is zero.
   */
  distribute(valueToDistribute: Money, proportions: Money[]): Money[];`;

export function moneyPreludeDts({
  includeDistribute = false,
}: MoneyPreludeOptions = {}): string {
  return `\
declare type Currency = ${currencyUnion};
declare type Money = { amount: number; currency: Currency };

declare const money: {
  /**
   * Converts an amount into another currency using the platform's exchange
   * rates. A no-op when already in the target currency. Throws if a rate for
   * either currency is unavailable.
   */
  convert(m: Money, currency: Currency): Money;
  /** Adds two amounts in the same currency. Throws on mismatch. */
  add(a: Money, b: Money): Money;
  /** Subtracts b from a (same currency). Throws on mismatch. */
  subtract(a: Money, b: Money): Money;
  /** Multiplies an amount by a scalar. */
  multiply(m: Money, n: number): Money;
  /** Sums an array of same-currency amounts. Empty array requires a currency arg via zero(). */
  sum(ms: Money[]): Money;
  /** Returns the larger of two same-currency amounts. */
  max(a: Money, b: Money): Money;
  /** Returns the smaller of two same-currency amounts. */
  min(a: Money, b: Money): Money;
  /** Clamps m between floor and ceiling (all same currency). */
  clamp(m: Money, floor: Money, ceiling: Money): Money;
  /** Returns a zero amount in the given currency (e.g. money.zero("USD")). */
  zero(currency: Currency): Money;${includeDistribute ? distributeDts : ""}
};
`;
}

const distributeJs = `
    distribute: (valueToDistribute, proportions) => {
      const minorUnits = (${minorUnitExceptionsJson})[valueToDistribute.currency] ?? 2;
      const factor = 10 ** minorUnits;
      if (!proportions || proportions.length === 0) {
        throw new Error("money.distribute requires a non-empty proportions array.");
      }
      const currency = valueToDistribute.currency;
      let totalProportion = 0;
      for (const p of proportions) {
        requireSameCurrency(valueToDistribute, p);
        if (p.amount < 0) {
          throw new Error("money.distribute proportions must be non-negative.");
        }
        totalProportion += p.amount;
      }
      if (totalProportion === 0) {
        return proportions.map(() => ({ amount: 0, currency }));
      }
      const sign = valueToDistribute.amount < 0 ? -1 : 1;
      const totalCents = Math.round(Math.abs(valueToDistribute.amount) * factor);
      const shares = proportions.map((p, index) => {
        const raw = (totalCents * p.amount) / totalProportion;
        const floor = Math.floor(raw);
        return { index, floor, remainder: raw - floor };
      });
      let leftover = totalCents - shares.reduce((acc, s) => acc + s.floor, 0);
      const byRemainder = [...shares].sort(
        (a, b) => b.remainder - a.remainder || a.index - b.index,
      );
      for (const share of byRemainder) {
        if (leftover <= 0) break;
        share.floor += 1;
        leftover -= 1;
      }
      return shares.map((s) => ({
        amount: (sign * s.floor) / factor,
        currency,
      }));
    },`;

/**
 * Runtime implementation of the `money` global. `convert` reads the ambient
 * `__redoRuntimeData.exchangeRates` table the platform injects at invoke time.
 */
export function moneyPreludeJs({
  includeDistribute = false,
}: MoneyPreludeOptions = {}): string {
  return `\
const money = (() => {
  const requireSameCurrency = (a, b) => {
    if (a.currency !== b.currency) {
      throw new Error(
        \`Currency mismatch: \${a.currency} vs \${b.currency}. Use money.convert() first.\`,
      );
    }
  };
  const exchangeRateTable = () => {
    const rates =
      typeof __redoRuntimeData !== "undefined" && __redoRuntimeData
        ? __redoRuntimeData.exchangeRates
        : undefined;
    if (!rates) {
      throw new Error(
        "Currency conversion is unavailable in this context.",
      );
    }
    return rates;
  };
  return {
    convert: (m, currency) => {
      if (m.currency === currency) {
        return m;
      }
      const rates = exchangeRateTable();
      const fromRate = rates[m.currency];
      const toRate = rates[currency];
      if (fromRate === undefined) {
        throw new Error(\`No exchange rate available for \${m.currency}.\`);
      }
      if (toRate === undefined) {
        throw new Error(\`No exchange rate available for \${currency}.\`);
      }
      return { amount: m.amount * (toRate / fromRate), currency };
    },
    add: (a, b) => {
      requireSameCurrency(a, b);
      return { amount: a.amount + b.amount, currency: a.currency };
    },
    subtract: (a, b) => {
      requireSameCurrency(a, b);
      return { amount: a.amount - b.amount, currency: a.currency };
    },
    multiply: (m, n) => ({ amount: m.amount * n, currency: m.currency }),
    sum: (ms) => {
      if (!ms || ms.length === 0) {
        throw new Error("money.sum requires a non-empty array; use money.zero() for an empty case.");
      }
      let acc = { amount: 0, currency: ms[0].currency };
      for (const m of ms) {
        requireSameCurrency(acc, m);
        acc = { amount: acc.amount + m.amount, currency: acc.currency };
      }
      return acc;
    },
    max: (a, b) => {
      requireSameCurrency(a, b);
      return a.amount >= b.amount ? a : b;
    },
    min: (a, b) => {
      requireSameCurrency(a, b);
      return a.amount <= b.amount ? a : b;
    },
    clamp: (m, floor, ceiling) => {
      requireSameCurrency(m, floor);
      requireSameCurrency(m, ceiling);
      if (m.amount < floor.amount) return floor;
      if (m.amount > ceiling.amount) return ceiling;
      return m;
    },
    zero: (currency) => ({ amount: 0, currency }),${includeDistribute ? distributeJs : ""}
  };
})();
`;
}
